import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, from } from 'rxjs';
import {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  updateUserAttributes,
  resendSignUpCode,
  fetchUserAttributes,
  type UserAttributeKey,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';

import { Config } from '../config/config';
import { IUser } from '../models/user.model';
import { UserInfo } from './user.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthStorageService } from './auth-storage.service';
import { UserInfoObservable, CreditCountObservable } from './observable.service';

@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  private authenticationSubject: BehaviorSubject<any>;

  private clearAllUserData(): void {
    this.authStorage.clearAuthData();
    this.userInfoObservable.clear();
    this.creditCountObservable.clear();
  }

  constructor(
    private router: Router, 
    private authStorage: AuthStorageService,
    private userInfoObservable: UserInfoObservable,
    private creditCountObservable: CreditCountObservable
  ) {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolClientId: Config.aws_user_pools_web_client_id,
          userPoolId: Config.aws_user_pools_id,
        },
      },
    });
    this.authenticationSubject = new BehaviorSubject<boolean>(false);
  }

  getLoggedInUser() {
    return this.authStorage.getItem('user') || null;
  }

  getLoggedInUserName(): string {
    const user = this.getLoggedInUser();
    return user.firstName || user.name || user.email;
  }

  public async signUp(user: IUser): Promise<any> {
    return signUp({
      username: user.email,
      password: user.password,
    });
  }

  public confirmSignUp(user: IUser): Promise<any> {
    return confirmSignUp({
      username: user.email,
      confirmationCode: user.code,
    });
  }

  public async signIn(user: IUser): Promise<any> {
    try {
      try {
        await signOut({ global: true });
        this.clearAllUserData();
      } catch (e) {}

      const signInResult = await signIn({
        username: user.email,
        password: user.password,
      });

      if (
        signInResult.isSignedIn &&
        signInResult.nextStep.signInStep === 'DONE'
      ) {
        const session = await fetchAuthSession();
        const attributes = await fetchUserAttributes();

        this.authStorage.setItem('user', attributes);
        this.authStorage.setItem(
          'accessToken',
          session.tokens?.accessToken?.toString() || ''
        );

        this.authenticationSubject.next(true);
        return {
          isSignedIn: true,
          nextStep: signInResult.nextStep,
          user: attributes,
        };
      }
      return {
        isSignedIn: false,
        nextStep: signInResult.nextStep,
      };
    } catch (error) {
      this.authenticationSubject.next(false);
      this.clearAllUserData();
      throw error;
    }
  }

  public async signOut(): Promise<any> {
    try {
      await signOut({ global: true });
    } catch (e) {}
    this.clearAllUserData();
    return this.authenticationSubject.next(false);
  }

  private async performSignOutAndRedirect(): Promise<boolean> {
    try {
      await signOut({ global: true });
    } catch (e) {
      console.error('Error during Cognito signout:', e);
    }
    this.clearAllUserData();
    this.authenticationSubject.next(false);
    await this.router.navigate(['/signIn'], { replaceUrl: true });
    return true;
  }

  public signOutAndRedirect(): Observable<boolean> {
    return from(this.performSignOutAndRedirect());
  }

  public async forgotPassword(user: IUser): Promise<any> {
    await resetPassword({
      username: user.email,
    });
    return this.authenticationSubject.next(true);
  }

  public async UpdateUserAttributes(data: any): Promise<any> {
    await updateUserAttributes({
      userAttributes: data.UserAttributes,
    });
  }

  public async forgotPasswordSubmit(user: IUser): Promise<any> {
    await confirmResetPassword({
      username: user.email,
      confirmationCode: user.code,
      newPassword: user.password,
    });
    return this.authenticationSubject.next(true);
  }

  public async isAuthenticated(): Promise<boolean> {
    if (this.authenticationSubject.value) {
      return Promise.resolve(true);
    } else {
      try {
        const user = await getCurrentUser();
        return !!user;
      } catch {
        return false;
      }
    }
  }

  public getUser(): Promise<any> {
    return getCurrentUser();
  }

  public async updateUser(user: UserInfo): Promise<any> {
    const userAttributes: Partial<Record<UserAttributeKey, string>> = {
      email: user.email,
    };
    return updateUserAttributes({
      userAttributes,
    });
  }

  public resendCode(email: string) {
    return resendSignUpCode({
      username: email,
    });
  }
}
